import React, { useState, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  LinearProgress,
  Alert,
} from "@mui/material";
import {
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  Image as ImageIcon,
  PictureAsPdf as PdfIcon,
  Videocam as VideoIcon,
  InsertDriveFile as FileIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
} from "@mui/icons-material";
import { useDropzone } from "react-dropzone";

interface UploadedFile {
  file: File;
  status: "pending" | "uploading" | "success" | "error";
  progress: number;
  url?: string;
  error?: string;
}

const FileUpload: React.FC = () => {
  const [files, setFiles] = useState<UploadedFile[]>([]);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const newFiles = acceptedFiles.map((file) => ({
      file,
      status: "pending" as const,
      progress: 0,
    }));
    setFiles((prev) => [...prev, ...newFiles]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    maxSize: 10 * 1024 * 1024, // 10MB
  });

  const getFileIcon = (file: File) => {
    if (file.type.startsWith("image/")) return <ImageIcon />;
    if (file.type === "application/pdf") return <PdfIcon />;
    if (file.type.startsWith("video/")) return <VideoIcon />;
    return <FileIcon />;
  };

  const getStatusIcon = (status: string) => {
    if (status === "success") return <SuccessIcon color="success" />;
    if (status === "error") return <ErrorIcon color="error" />;
    return null;
  };

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const uploadFile = async (index: number) => {
    // Update status to uploading
    setFiles((prev) =>
      prev.map((f, i) => (i === index ? { ...f, status: "uploading" } : f))
    );

    // Simulate upload progress
    for (let progress = 0; progress <= 100; progress += 10) {
      await new Promise((resolve) => setTimeout(resolve, 100));
      setFiles((prev) =>
        prev.map((f, i) => (i === index ? { ...f, progress } : f))
      );
    }

    // Simulate upload completion
    const success = Math.random() > 0.2; // 80% success rate
    setFiles((prev) =>
      prev.map((f, i) =>
        i === index
          ? {
              ...f,
              status: success ? "success" : "error",
              url: success ? URL.createObjectURL(f.file) : undefined,
              error: success ? undefined : "Upload failed. Please try again.",
            }
          : f
      )
    );
  };

  const uploadAll = () => {
    files.forEach((file, index) => {
      if (file.status === "pending") {
        uploadFile(index);
      }
    });
  };

  const clearAll = () => {
    setFiles([]);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        File Upload Testing
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper>
            <Box
              {...getRootProps()}
              sx={{
                p: 4,
                border: "2px dashed",
                borderColor: isDragActive ? "primary.main" : "divider",
                borderRadius: 2,
                textAlign: "center",
                cursor: "pointer",
                backgroundColor: isDragActive
                  ? "action.hover"
                  : "background.paper",
                transition: "all 0.3s",
              }}
            >
              <input {...getInputProps()} />
              <UploadIcon
                sx={{ fontSize: 64, color: "text.secondary", mb: 2 }}
              />
              <Typography variant="h6" gutterBottom>
                {isDragActive
                  ? "Drop the files here..."
                  : "Drag & drop files here, or click to select"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Maximum file size: 10MB
              </Typography>
            </Box>
          </Paper>

          <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
            <Button
              variant="contained"
              onClick={uploadAll}
              disabled={
                files.length === 0 || files.every((f) => f.status !== "pending")
              }
              fullWidth
            >
              Upload All
            </Button>
            <Button
              variant="outlined"
              onClick={clearAll}
              disabled={files.length === 0}
              fullWidth
            >
              Clear All
            </Button>
          </Box>

          <Alert severity="info" sx={{ mt: 3 }}>
            <Typography variant="body2">
              This is a test upload interface. Files are not actually uploaded
              to a server. The upload simulation demonstrates progress tracking
              and error handling.
            </Typography>
          </Alert>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Files ({files.length})
            </Typography>

            {files.length === 0 ? (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ p: 2, textAlign: "center" }}
              >
                No files selected
              </Typography>
            ) : (
              <List>
                {files.map((fileItem, index) => (
                  <ListItem
                    key={index}
                    secondaryAction={
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        {getStatusIcon(fileItem.status)}
                        <IconButton
                          edge="end"
                          onClick={() => removeFile(index)}
                          disabled={fileItem.status === "uploading"}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    }
                  >
                    <ListItemIcon>{getFileIcon(fileItem.file)}</ListItemIcon>
                    <ListItemText
                      primary={fileItem.file.name}
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            {formatFileSize(fileItem.file.size)}
                            {fileItem.status === "uploading" &&
                              ` - ${fileItem.progress}%`}
                            {fileItem.error && ` - ${fileItem.error}`}
                          </Typography>
                          {fileItem.status === "uploading" && (
                            <LinearProgress
                              variant="determinate"
                              value={fileItem.progress}
                              sx={{ mt: 1 }}
                            />
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Paper>

          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Upload Statistics
              </Typography>
              <Typography variant="body2">
                Total files: {files.length}
              </Typography>
              <Typography variant="body2">
                Pending: {files.filter((f) => f.status === "pending").length}
              </Typography>
              <Typography variant="body2">
                Uploading:{" "}
                {files.filter((f) => f.status === "uploading").length}
              </Typography>
              <Typography variant="body2">
                Success: {files.filter((f) => f.status === "success").length}
              </Typography>
              <Typography variant="body2">
                Failed: {files.filter((f) => f.status === "error").length}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                Total size:{" "}
                {formatFileSize(files.reduce((sum, f) => sum + f.file.size, 0))}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default FileUpload;
